import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Badge, CircularProgress, Fab } from '@mui/material'
import { makeStyles } from '@mui/styles'
import Draggable from 'react-draggable'
import type { DraggableData, DraggableEvent } from 'react-draggable'
import AiAssistantOrb from '@ai-chat-widget/assistant-orb'
import { useAppSelector } from '@/redux/store'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import type { AiChatWebSocketEventData } from '@/modules/webSocket/types'
import {
  AI_CHAT_LAUNCHER_MARGIN,
  AI_CHAT_LAUNCHER_FALLBACK_SIZE,
  AI_CHAT_BUTTON_POSITION_STORAGE_KEY,
  AI_CHAT_PERSISTED_TRIGGER_MARGIN,
  toPersistedTriggerPosition,
  toRenderedTriggerPosition,
  toViewportStableRenderedTriggerPosition,
  withViewportRelativeTriggerPosition,
} from '@ai-chat-widget-contracts/contracts'
import type { AiChatDockLayout, AiChatTriggerPosition, AiWidgetAuthorizationDebug, AiWidgetSubscriptionDebug, AiWidgetStrings, ChatResponseEvent, WidgetUser } from '@ai-chat-widget-contracts/contracts'
import { useLocation } from 'react-router'
import type { DetailsType } from '@/redux/ducks/subscription/types/details'
import { ROLES } from '@/modules/base/util/constants'
import { useAiWidgetRuntime } from './runtime'
import { resolvePageContext } from './resolve-page-context'
import { resolvePersona } from './resolve-persona'
import { toAiChatResponseEvent } from './response-event'
import { defaultStrings } from '@ai-chat-widget/default-strings'
import type ThemeInterface from '@/theme-interface'

const RemoteAiChatWidget = React.lazy(() => import('aiChatWidget/AiChatWidget'))
const hostLauncherCoordinates = {
  placement: 'bottom-right' as const,
  visualMargin: AI_CHAT_LAUNCHER_MARGIN,
  persistedMargin: AI_CHAT_PERSISTED_TRIGGER_MARGIN,
}

const toDebugDate = (value: Date | string | null | undefined): string | null => {
  if (!value) return null
  return value instanceof Date ? value.toISOString() : String(value)
}

const createSubscriptionDebug = (subscriptionDetails: Partial<DetailsType> = {}): AiWidgetSubscriptionDebug => {
  const subscriptions = (subscriptionDetails.subscriptions || []).map(subscription => ({
    id: subscription.id,
    type: subscription.type,
    typeOfSubscription: subscription.typeOfSubscription,
    title: subscription.title,
    begin: toDebugDate(subscription.begin),
    end: toDebugDate(subscription.end),
    isTrial: subscription.isTrial,
    isCancelled: subscription.isCancelled,
    isExpired: subscription.isExpired,
    isSubscriptionLocked: subscription.isSubscriptionLocked,
  }))
  const selectedSubscriptionId = subscriptionDetails.selectedSubscriptionId ?? null

  return {
    loading: subscriptionDetails.loading !== false,
    error: Boolean(subscriptionDetails.error),
    lastFetch: toDebugDate(subscriptionDetails.lastFetch),
    subscriptionCount: subscriptions.length,
    current: subscriptions.find(subscription => subscription.id === selectedSubscriptionId) || subscriptions[0] || null,
  }
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  launcherViewport: {
    position: 'fixed',
    inset: 0,
    zIndex: theme.zIndex.appBar + 1,
    pointerEvents: 'none',
  },
  launcher: {
    position: 'absolute',
    right: AI_CHAT_LAUNCHER_MARGIN,
    bottom: AI_CHAT_LAUNCHER_MARGIN,
    pointerEvents: 'auto',
    touchAction: 'none',
  },
  button: {
    position: 'relative',
    padding: '0 !important',
    overflow: 'hidden',
    backgroundColor: 'transparent !important',
    color: `${theme.white} !important`,
    transition: 'transform 150ms ease',
    '&:hover': {
      backgroundColor: 'transparent !important',
      transform: 'scale(1.08)',
    },
    '&.Mui-disabled': {
      backgroundColor: 'transparent !important',
    },
    '&:focus-visible': {
      outline: `2px solid ${theme.primaryV2}`,
      outlineOffset: 2,
    },
  },
  loadingIndicator: {
    position: 'absolute',
    inset: 0,
    margin: 'auto',
    color: `${theme.white} !important`,
  },
}))

const readHostLauncherPosition = (): AiChatTriggerPosition | null => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(AI_CHAT_BUTTON_POSITION_STORAGE_KEY) || 'null') as Partial<AiChatTriggerPosition> | null
    return parsed && Number.isFinite(parsed.x) && Number.isFinite(parsed.y)
      ? {
        x: Number(parsed.x),
        y: Number(parsed.y),
        ...(Number.isFinite(parsed.viewportXRatio) && Number.isFinite(parsed.viewportYRatio)
          ? { viewportXRatio: Number(parsed.viewportXRatio), viewportYRatio: Number(parsed.viewportYRatio) }
          : {}),
      }
      : null
  } catch {
    return null
  }
}

const writeHostLauncherPosition = (position: AiChatTriggerPosition) => {
  try {
    window.localStorage.setItem(AI_CHAT_BUTTON_POSITION_STORAGE_KEY, JSON.stringify(position))
  } catch {
    // The launcher remains draggable for the current page view without storage.
  }
}

const useHostLauncherPosition = () => {
  const triggerRef = useRef<HTMLDivElement>(null)
  const [storedPosition, setStoredPosition] = useState<AiChatTriggerPosition | null>(readHostLauncherPosition)
  const [isDragging, setIsDragging] = useState(false)
  const [layout, setLayout] = useState(() => ({
    viewport: {
      width: document.documentElement.clientWidth || window.innerWidth,
      height: document.documentElement.clientHeight || window.innerHeight,
    },
    triggerSize: { width: AI_CHAT_LAUNCHER_FALLBACK_SIZE, height: AI_CHAT_LAUNCHER_FALLBACK_SIZE },
  }))

  useEffect(() => {
    const updateLayout = () => {
      const element = triggerRef.current
      if (!element) return
      const rect = element.getBoundingClientRect()
      const nextLayout = {
        viewport: {
          width: document.documentElement.clientWidth || window.innerWidth,
          height: document.documentElement.clientHeight || window.innerHeight,
        },
        triggerSize: { width: rect.width, height: rect.height },
      }
      setLayout(current => (
        current.viewport.width === nextLayout.viewport.width
        && current.viewport.height === nextLayout.viewport.height
        && current.triggerSize.width === nextLayout.triggerSize.width
        && current.triggerSize.height === nextLayout.triggerSize.height
          ? current
          : nextLayout
      ))
    }

    updateLayout()
    window.addEventListener('resize', updateLayout)
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateLayout)
    if (triggerRef.current) resizeObserver?.observe(triggerRef.current)
    return () => {
      window.removeEventListener('resize', updateLayout)
      resizeObserver?.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!storedPosition || (Number.isFinite(storedPosition.viewportXRatio) && Number.isFinite(storedPosition.viewportYRatio)) || !triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const viewport = {
      width: document.documentElement.clientWidth || window.innerWidth,
      height: document.documentElement.clientHeight || window.innerHeight,
    }
    const renderedPosition = toRenderedTriggerPosition(storedPosition, hostLauncherCoordinates)
    const nextPosition = withViewportRelativeTriggerPosition(
      storedPosition,
      {
        left: viewport.width - rect.width - hostLauncherCoordinates.visualMargin + renderedPosition.x,
        top: viewport.height - rect.height - hostLauncherCoordinates.visualMargin + renderedPosition.y,
        width: rect.width,
        height: rect.height,
      },
      viewport,
    )
    setStoredPosition(nextPosition)
    writeHostLauncherPosition(nextPosition)
  }, [storedPosition])

  const renderedPosition = useMemo(() => (
    storedPosition
      ? toViewportStableRenderedTriggerPosition(storedPosition, hostLauncherCoordinates, layout.viewport, layout.triggerSize)
      : undefined
  ), [layout, storedPosition])

  const handleDragStop = useCallback((_event: DraggableEvent, data: DraggableData) => {
    const persistedPosition = toPersistedTriggerPosition({ x: data.x, y: data.y }, hostLauncherCoordinates)
    const rect = triggerRef.current?.getBoundingClientRect()
    const nextPosition = rect
      ? withViewportRelativeTriggerPosition(persistedPosition, rect, {
        width: document.documentElement.clientWidth || window.innerWidth,
        height: document.documentElement.clientHeight || window.innerHeight,
      })
      : persistedPosition
    setStoredPosition(nextPosition)
    writeHostLauncherPosition(nextPosition)
    setIsDragging(false)
  }, [])

  return {
    triggerRef,
    renderedPosition,
    isDragging,
    handleDragStart: () => setIsDragging(true),
    handleDragStop,
  }
}

interface LauncherProps {
  loading?: boolean
  unreadCount: number
  onActivate: () => void
  strings: AiWidgetStrings
}

const AiChatHostLauncher = ({ loading = false, unreadCount, onActivate, strings }: LauncherProps) => {
  const classes = useStyles()
  const pointerStartRef = useRef<{ x: number, y: number, time: number } | null>(null)
  const { triggerRef, renderedPosition, isDragging, handleDragStart, handleDragStop } = useHostLauncherPosition()

  const setLauncherRef = useCallback((node: HTMLDivElement | null) => {
    triggerRef.current = node
  }, [triggerRef])

  const handlePointerDown = (event: React.PointerEvent) => {
    pointerStartRef.current = { x: event.clientX, y: event.clientY, time: Date.now() }
  }

  const handlePointerUp = (event: React.PointerEvent) => {
    if (!pointerStartRef.current) return
    const { x, y, time } = pointerStartRef.current
    pointerStartRef.current = null
    if (Math.abs(event.clientX - x) < 10 && Math.abs(event.clientY - y) < 10 && Date.now() - time < 300) onActivate()
  }

  return (
    <div className={classes.launcherViewport}>
      <Draggable
        position={renderedPosition}
        onStart={handleDragStart}
        onStop={handleDragStop}
        bounds="parent"
        nodeRef={triggerRef}
      >
        <div ref={setLauncherRef} className={classes.launcher} data-ai-chat-button="true">
          <Badge badgeContent={unreadCount} color="error" max={99} overlap="circular" invisible={unreadCount === 0}>
            <Fab
              className={classes.button}
              aria-label={strings.ai.chat.button || defaultStrings.ai.chat.button}
              onPointerDown={handlePointerDown}
              onPointerUp={handlePointerUp}
              onClick={event => { if (event.detail === 0) onActivate() }}
              disabled={loading}
              aria-busy={loading || undefined}
              data-qs-id="ai-chat-button-toggle"
              sx={{ cursor: isDragging ? 'grabbing !important' : 'pointer' }}
            >
              <AiAssistantOrb size={56} processing={loading} />
              {loading && <CircularProgress className={classes.loadingIndicator} size={24} color="inherit" />}
            </Fab>
          </Badge>
        </div>
      </Draggable>
    </div>
  )
}

interface AiChatWidgetLoaderProps {
  onDockLayoutChange?: (layout: AiChatDockLayout) => void
}

export default function AiChatWidgetLoader({ onDockLayoutChange }: AiChatWidgetLoaderProps) {
  const runtime = useAiWidgetRuntime()
  const location = useLocation()
  const sequence = useRef(0)
  const [responseEvents, setResponseEvents] = useState<ChatResponseEvent[]>([])
  const [responseEventsTenantKey, setResponseEventsTenantKey] = useState('')
  const [activated, setActivated] = useState(false)
  const [initialOpenPending, setInitialOpenPending] = useState(false)
  const userInfo = useAppSelector(state => state.user.info)
  const subscriptionDetails = useAppSelector(state => state.subscription.details)
  const activeTenantUserId = userInfo.activeTenant?.tenantUserId
  const activeTenantKey = String(activeTenantUserId || '')

  useEffect(() => {
    // A tenant change starts a fully isolated widget session. Keep the queue key
    // separate from the active key so the first render cannot pass stale events on.
    sequence.current = 0
    setResponseEvents([])
    setResponseEventsTenantKey(activeTenantKey)
    setInitialOpenPending(false)
    setActivated(false)
  }, [activeTenantKey])

  useWebSocketEvent(EventTypes.AI_CHAT_RESPONSE, useCallback((event: AiChatWebSocketEventData) => {
    if (!activeTenantKey) return
    const normalizedEvent = toAiChatResponseEvent(event.data, activeTenantUserId, sequence.current + 1)
    if (!normalizedEvent) return
    sequence.current = normalizedEvent.sequence
    setResponseEvents(current => [...current, normalizedEvent])
    setResponseEventsTenantKey(activeTenantKey)
  }, [activeTenantKey, activeTenantUserId]))

  const handleEventsProcessed = useCallback((throughSequence: number) => {
    setResponseEvents(current => current.filter(event => event.sequence > throughSequence))
  }, [])

  const handleActivate = useCallback(() => {
    setActivated(true)
    setInitialOpenPending(true)
  }, [])

  const handleInitialOpenApplied = useCallback(() => setInitialOpenPending(false), [])

  const persona = resolvePersona(userInfo, subscriptionDetails)
  const authorization = useMemo<AiWidgetAuthorizationDebug>(() => ({
    isOwner: Boolean(userInfo.isOwner),
    isTenantAdmin: (userInfo.roles || []).includes(ROLES.ADMIN_ROLE),
    isSupportAdmin: Boolean(userInfo.isAdminLogin),
  }), [userInfo])

  const user = useMemo<WidgetUser>(() => ({
    sessionKey: activeTenantKey,
    displayName: [userInfo.firstName, userInfo.lastName].filter(Boolean).join(' ') || userInfo.activeTenant?.tenantName || '',
    firstName: userInfo.firstName,
    tenantName: userInfo.activeTenant?.tenantName,
    companyName: userInfo.activeTenant?.companyName,
    avatarUrl: userInfo.avatarImageUrl,
    colors: {
      backgroundColor: userInfo.activeTenant?.backgroundColor,
      color: userInfo.activeTenant?.color,
    },
    persona,
  }), [activeTenantKey, activeTenantUserId, persona, subscriptionDetails, userInfo])

  const widgetRuntime = useMemo(() => ({
    ...runtime,
    quickActions: runtime.quickActions?.filter(action => action.id !== 'appointment' || persona === 'trial' || persona === 'churned'),
    debug: runtime.debug
      ? {
        ...runtime.debug,
        subscription: createSubscriptionDebug(subscriptionDetails),
        ...(runtime.debug.enabled ? { authorization } : {}),
      }
      : undefined,
  }), [authorization, persona, runtime, subscriptionDetails])

  const pageContext = useMemo(() => resolvePageContext(location.pathname), [location.pathname])

  const activeResponseEvents = responseEventsTenantKey === activeTenantKey ? responseEvents : []
  const unreadCount = useMemo(() => new Set(activeResponseEvents.map(event => event.conversationId)).size, [activeResponseEvents])

  if (!activeTenantKey || !activated) {
    return <AiChatHostLauncher strings={runtime.strings} unreadCount={unreadCount} onActivate={handleActivate} />
  }

  return (
    <Suspense fallback={<AiChatHostLauncher strings={runtime.strings} loading unreadCount={unreadCount} onActivate={() => undefined} />}>
      <RemoteAiChatWidget
        key={activeTenantKey}
        runtime={widgetRuntime}
        pageContext={pageContext}
        user={user}
        responseEvents={activeResponseEvents}
        initialOpen={initialOpenPending}
        onInitialOpenApplied={handleInitialOpenApplied}
        onResponseEventsProcessed={handleEventsProcessed}
        onDockLayoutChange={onDockLayoutChange}
      />
    </Suspense>
  )
}
